// «Изменить у всех»: правка многих размещённых вакансий одним действием.
//
// ## Почему панель, а не модалка
//
// Правило 4: модалка оставлена необратимому подтверждению с итоговой цифрой. Здесь работа —
// человек набирает, что менять, смотрит, что получится, правит формулировку и смотрит снова.
// Модалка, из которой нельзя вернуться к набору, превратила бы это в шесть заходов.
//
// ## Предпросмотр обязателен, и он не украшение
//
// Правка идёт по многим объявлениям сразу, и ошибка в ней стоит не одного, а всех. Правило 6
// требует показывать «ляжет N значений, отклонено M и почему» ДО применения — здесь то же
// самое: сначала «посмотреть, что получится», и только потом «применить».
//
// Кнопка «Применить» не появляется, пока не смотрели: применить вслепую то, что уедет на
// площадку по сотне объявлений, нельзя.
//
// ## Помехи считает СЕРВЕР
//
// Дубль возникает как итог правки — каждая строка по отдельности законна. Посчитать это можно,
// только зная будущие тексты всех объявлений сразу, включая те, что стоят под другими учётными
// записями. На клиенте такой проверки быть не может, и подделывать её нельзя: она врала бы
// ровно там, где нужна.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiEdit2Line, RiEyeLine } from '@remixicon/react';

import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import type { BulkEditOut } from '../api/platform';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useBulkEditMutation } from '../queries';

/** Вакансии ведёт рекрутёр: у него пакет размещения (матрица тз, раздел 3.2). */
const PUBLISH_PERMISSION = 'hiring.avito.publish';
const PUBLISH_OWNER = 'Вакансии на площадке ведёт рекрутёр — попросите его.';

interface Форма {
  city: string;
  find: string;
  replace: string;
  title: string;
  workplace: string;
}

export function BulkEditPanel({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const разрешено = useCan(PUBLISH_PERMISSION);
  const mutation = useBulkEditMutation();
  const [форма, setФорма] = useState<Форма>({
    city: '',
    find: '',
    replace: '',
    title: '',
    workplace: '',
  });
  const [итог, setИтог] = useState<BulkEditOut | null>(null);
  const [заметка, setЗаметка] = useState<string | null>(null);

  function поправить(часть: Partial<Форма>) {
    setФорма((текущее) => ({ ...текущее, ...часть }));
    // Любая правка условий делает прежний предпросмотр неправдой. Оставить его на экране
    // значило бы дать нажать «Применить» под цифры, которых уже нет.
    setИтог(null);
  }

  function собрать(apply: boolean) {
    return {
      account_id: accountId,
      city: форма.city.trim() || null,
      find: форма.find.trim() || null,
      replace: форма.find.trim() ? форма.replace : null,
      title: форма.title.trim() || null,
      workplace: форма.workplace.trim() || null,
      apply,
    };
  }

  function пустая(): boolean {
    return (
      форма.find.trim() === '' && форма.title.trim() === '' && форма.workplace.trim() === ''
    );
  }

  function посмотреть(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!разрешено) {
      setЗаметка(`Права менять вакансии на площадке у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    if (пустая()) {
      setЗаметка('Нечего менять: заполните хотя бы одно поле — замену в тексте, заголовок или место работы.');
      return;
    }
    setЗаметка(null);
    mutation.mutate(собрать(false), { onSuccess: setИтог });
  }

  function применить() {
    if (итог === null) {
      return;
    }
    if (mutation.isPending) {
      setЗаметка('Правка уже применяется, ждём ответ площадки. Второй раз не отправляем.');
      return;
    }
    setЗаметка(null);
    mutation.mutate(собрать(true), {
      onSuccess: (свежий) => {
        setИтог(свежий);
        if (свежий.с_помехой === 0) {
          onClose();
        }
      },
    });
  }

  return (
    <form onSubmit={посмотреть} noValidate className='flex flex-col gap-3'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Правка уходит на площадку по каждому размещённому объявлению записи. Черновики не
        трогаются: менять на площадке у них нечего.
      </p>

      {!разрешено && (
        <NoPermissionNote что='менять вакансии на площадке' кто={PUBLISH_OWNER} />
      )}

      <TextField
        id='bulk-city'
        label='Только город'
        value={форма.city}
        onChange={(значение) => поправить({ city: значение })}
        placeholder='Пусто — все города записи'
      />

      <TextField
        id='bulk-find'
        label='Найти в тексте'
        value={форма.find}
        onChange={(значение) => поправить({ find: значение })}
        placeholder='60/30'
      />

      <TextArea
        id='bulk-replace'
        label='Заменить на'
        value={форма.replace}
        onChange={(значение) => поправить({ replace: значение })}
        placeholder='45/45'
        rows={2}
      />

      <TextField
        id='bulk-title'
        label='Новый заголовок'
        value={форма.title}
        onChange={(значение) => поправить({ title: значение })}
        placeholder='Пусто — заголовок не трогаем'
      />

      <TextField
        id='bulk-workplace'
        label='Новое место работы'
        value={форма.workplace}
        onChange={(значение) => поправить({ workplace: значение })}
        placeholder='Пусто — место работы не трогаем'
      />

      {заметка !== null && <GateNote>{заметка}</GateNote>}
      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {итог !== null && <Предпросмотр итог={итог} />}

      <div className='flex flex-wrap gap-2'>
        <Button.Root type='submit' variant='neutral' mode='stroke' size='small'>
          <Button.Icon as={RiEyeLine} />
          {mutation.isPending && !mutation.variables?.apply
            ? 'Считаем…'
            : 'Посмотреть, что получится'}
        </Button.Root>
        {итог !== null && итог.меняется > 0 && (
          <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={применить}>
            <Button.Icon as={RiEdit2Line} />
            {mutation.isPending && mutation.variables?.apply
              ? 'Меняем…'
              : `Применить к ${итог.меняется}`}
          </Button.Root>
        )}
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Закрыть
        </Button.Root>
      </div>
    </form>
  );
}

/** Что произойдёт: сколько изменится, что этому мешает и как будет выглядеть текст. */
function Предпросмотр({ итог }: { итог: BulkEditOut }) {
  const помехи = итог.items.filter((строка) => строка.помеха !== null);
  const меняются = итог.items.filter((строка) => строка.меняется && строка.помеха === null);

  return (
    <div className='flex flex-col gap-2'>
      <Alert.Root
        variant='light'
        status={итог.с_помехой > 0 ? 'warning' : 'information'}
        size='small'
        role='status'
      >
        <Alert.Icon as={итог.применяли ? RiEdit2Line : RiEyeLine} />
        <span>
          {итог.применяли
            ? `Изменено ${итог.применено} из ${итог.total}.`
            : `Под отбор попало ${итог.total}, изменится ${итог.меняется}.`}
          {итог.с_помехой > 0 && ` Не пройдёт ${итог.с_помехой} — причины ниже.`}
        </span>
      </Alert.Root>

      {помехи.length > 0 && (
        <ul className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 p-2'>
          {помехи.slice(0, 8).map((строка) => (
            <li key={строка.id} className='text-paragraph-xs text-text-sub-600'>
              <span className='text-text-strong-950'>{строка.title}</span>, {строка.city} —{' '}
              {строка.помеха}
            </li>
          ))}
        </ul>
      )}

      {меняются.length > 0 && (
        <ul className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 p-2'>
          {меняются.slice(0, 5).map((строка) => (
            <li key={строка.id} className='flex flex-col gap-0.5'>
              <span className='text-paragraph-xs text-text-sub-600'>
                {строка.title}, {строка.city}
              </span>
              {/* Прежний текст отличается зачёркиванием, а не бледностью: тон soft-400 даёт
                  на белом 2,63:1 при норме 4,5, и им нельзя писать смысл (проверка
                  «бледно-серым не пишут текст»). */}
              <span className='text-paragraph-xs text-text-sub-600 line-through'>
                {строка.было}
              </span>
              <span className='text-paragraph-xs text-text-strong-950'>{строка.станет}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
